import lodash from "lodash";
import dayjs, { OpUnitType, QUnitType } from "dayjs";
import { utils as xlsxUtils, writeFile } from "xlsx-js-style";
import { api } from "@/comn";
import {
    formatText,
    formatNumber,
    formatCheckbox,
    formatDate,
    formatTime,
    formatDatetime,
    unformatText,
    unformatNumber,
    unformatCheckbox,
    unformatDate,
    unformatTime,
    unformatDatetime,
    unformatCode,
    viewCheckbox,
    localeDate,
    localeDatetime,
    localeTime,
    viewRadio,
    viewSelect,
} from "@/comn/components/_";
import { BOLD_TEXT, COLOR_TEXT, SIZE_TEXT } from "../features/foundation";
import Cookies from "js-cookie";
import { comnEnvs } from "./comnEnvs";
import { getCode, getCodeLabel, getCodeOptions, getCodeValue } from "./comnCodes";

export const comnUtils = {
    // 코드 유틸
    getCode,
    getCodeLabel,
    getCodeValue,
    getCodeOptions,
    getInvalidMessage: (t: any, invalid: any, props: any, message: any) => {
        // console.log(invalid, props);
        if (!invalid) return t(message);
        if (invalid.type === "minLength") {
            return t("msg.osc.00020", { 0: props.minLength });
        } else if (invalid.type === "maxLength") {
            return t("msg.osc.00021", { 0: props.maxLength });
        } else if (invalid.type === "min") {
            return t("msg.osc.00016", { 0: props.min });
        } else if (invalid.type === "max") {
            return t("msg.osc.00018", { 0: props.max });
        } else if (invalid.type === "pattern") {
            return t("msg.osc.00022");
        } else {
            if (invalid.message) return t(invalid.message);
            return t(message);
        }
    },
    getFormattedNumber: (value: any, decimalScale: number = 0) => {
        return formatNumber(value, { thousandSeparator: true, decimalScale: decimalScale });
    },
    getFormattedValue: (value: any, schema?: any) => {
        switch (schema?.type) {
            case "text":
                return formatText(value, schema);
            case "number":
                return formatNumber(value, schema);
            case "checkbox":
                return formatCheckbox(value);
            case "date":
                return formatDate(value);
            case "time":
                return formatTime(value);
            case "datetime":
                return formatDatetime(value);
            case "radio":
                return value;
            //default:
            //    return formatText(v, s);
        }
        return value;
    },
    getUnformattedValue: (v: any, s?: any) => {
        switch (s?.type) {
            case "text":
                return unformatText(v, s);
            case "number":
                return unformatNumber(v, s);
            case "checkbox":
                return unformatCheckbox(v, s);
            case "date":
                return unformatDate(v, s);
            case "time":
                return unformatTime(v, s);
            case "datetime":
                return unformatDatetime(v, s);
            case "code":
                return unformatCode(v, s);
            case "file":
                if (Boolean(v) && typeof v === "object") {
                    if (typeof v.fileGrpUuid === "string" && Array.isArray(v.fileSaveReqDtoList)) {
                        return v;
                    }
                }
                return undefined;
        }
        return v;
    },
    getViewValue: (v: any, s?: any) => {
        switch (s?.type) {
            case "text":
                return v;
            case "number":
                return v;
            case "checkbox":
                return viewCheckbox(v, { options: s?.options, viewType: s?.viewType });
            case "select":
                return viewSelect(v, { options: s?.options, viewType: s?.viewType });
            case "date":
                return localeDate(v, comnUtils.getLocale());
            case "time":
                return localeTime(v, comnUtils.getLocale());
            case "datetime":
                return localeDatetime(v, comnUtils.getLocale());
            case "radio":
                return viewRadio(v, s);
        }
        return v;
    },
    getGridData: (content: Record<string, any>) => {
        return {
            content,
            __t: new Date(),
            page: { totalElements: content.length },
        };
    },
    getLocale: (): "ko" | "en" | "tz" => {
        if (Cookies.get("lang")?.toString() === "ko") return "ko";
        else if (Cookies.get("lang")?.toString() === "tz") return "tz";
        return "en";
    },
    getLocaleString: () => {
        let locale = Cookies.get("lang")?.toString() || "en";
        if (locale === "tz") return comnEnvs.locale.tz;
        else if (locale === "en") return comnEnvs.locale.en;
        else return comnEnvs.locale.ko;
    },
    isUndefined: (arg: any) => {
        return arg === undefined;
    },
    isNull: (arg: any) => {
        return arg === null || arg === "null";
    },
    isEmptyString: (arg: any) => {
        return arg === "";
    },
    isEmptyArray: (arg: any) => {
        return Array.isArray(arg) && arg.length === 0;
    },
    isEmptyObject: (arg: any) => {
        return lodash.isEmpty(arg);
    },
    isEmpty: (arg: any): boolean => {
        if (typeof arg === "number") return false;
        return lodash.isEmpty(arg);
    },
    replaceEmpty: (arg: any, replace: any = "") => {
        if (comnUtils.isUndefined(arg) || comnUtils.isNull(arg)) return replace;
        return arg;
    },
    encodeURI: (str: string) => {
        return encodeURIComponent(str);
    },
    decodeURI: (str: string) => {
        return decodeURIComponent(str);
    },
    getDate: (
        args: string | { date?: Date; y?: number; m?: number; d?: number; h?: number; mi?: number } = {
            date: new Date(),
            y: 0,
            m: 0,
            d: 0,
            h: 0,
            mi: 0,
        },
    ): Date => {
        if (args === undefined || args === null) return new Date();

        if (typeof args === "string") {
            return dayjs(args).toDate();
        } else {
            const { date, y, m, d, h, mi } = args;
            let temp = dayjs(date);
            temp = temp
                .set("y", temp.get("y") + (y || 0))
                .set("M", temp.get("M") + (m || 0))
                .set("D", temp.get("D") + (d || 0))
                .set("h", temp.get("h") + (h || 0))
                .set("m", temp.get("m") + (mi || 0));
            return temp.toDate();
        }
    },
    compareDate: (a: any, b: any) => {
        if (typeof a === "string") {
            a = dayjs(a).toDate();
        }
        if (typeof b === "string") {
            b = dayjs(b).toDate();
        }
        a = comnUtils.dateToString(a, "date");
        b = comnUtils.dateToString(b, "date");
        if (a > b) return -1;
        if (a < b) return 1;
        if (a === b) return 0;
    },
    compareTime: (a: any, b: any) => {
        if (typeof a === "string") {
            a = dayjs(a).toDate();
        }
        if (typeof b === "string") {
            b = dayjs(b).toDate();
        }
        a = comnUtils.dateToString(a, "time");
        b = comnUtils.dateToString(b, "time");
        if (a > b) return -1;
        if (a < b) return 1;
        if (a === b) return 0;
    },
    compareDatetime: (a: any, b: any) => {
        if (typeof a === "string") {
            a = dayjs(a).toDate();
        }
        if (typeof b === "string") {
            b = dayjs(b).toDate();
        }
        a = comnUtils.dateToString(a, "datetime");
        b = comnUtils.dateToString(b, "datetime");
        if (a > b) return -1;
        if (a < b) return 1;
        if (a === b) return 0;
    },
    dateToString: (date: Date, format?: any | "date" | "time" | "datetime") => {
        return dayjs(date).format(
            format === undefined || format === "date" ? "YYYY-MM-DD" : format === "time" ? "HH:mm" : "YYYY-MM-DD HH:mm",
        );
    },

    findIndex: (array: Array<any>, obj: any) => {
        return lodash.findIndex(array, obj);
    },

    toGetParams: (obj: any) => {
        if (lodash.isEmpty(obj)) return obj;

        const _obj = lodash.cloneDeep(obj);
        Object.keys(obj).forEach((key) => {
            if (lodash.isArray(obj[key])) {
                _obj[key] = obj[key].join(",");
            } else {
                _obj[key] = obj[key];
            }
        });

        return _obj;
    },
    toValues: (obj: any) => {
        if (lodash.isEmpty(obj)) return obj;
        const _obj = lodash.cloneDeep(obj);
        Object.keys(obj).forEach((key) => {
            if (typeof obj[key] === "function") {
                _obj[key] = obj[key]();
            } else {
                _obj[key] = obj[key];
            }
        });

        return _obj;
    },
    setValuesFromParams: (form: any, params: any) => {
        form.setValues(params, false);
    },
    isPopup: () => {
        const search = new URLSearchParams(window.location.search);
        return search.get("ppup") === "Y" ? true : window.opener ? true : false;
    },
    equals: (first: object, second: object) => {
        return lodash.isEqual(first, second);
    },
    postMessage: (data: any) => {
        if (window.opener) {
            window.opener.postMessage(data);
        }

        if (window.parent) {
            window.parent.postMessage(data);
        }
    },
    getViewStyle: (
        color: keyof typeof COLOR_TEXT | undefined,
        bold: keyof typeof BOLD_TEXT | undefined,
        fontSize: keyof typeof SIZE_TEXT | undefined,
    ) => {
        let className = "";
        if (color !== undefined) className += " " + COLOR_TEXT[color];
        if (bold !== undefined) className += " " + BOLD_TEXT[bold];
        if (fontSize !== undefined) className += " text-" + fontSize;
        return className;
    },
    getEditStyle: (color: keyof typeof COLOR_TEXT | undefined, bold: keyof typeof BOLD_TEXT | undefined) => {
        let className = "";
        if (color !== undefined) className += " " + COLOR_TEXT[color];
        if (bold !== undefined) className += " " + BOLD_TEXT[bold];
        return className;
    },
    getOptions: (content: any, key: string, label: string) => {
        if (!Array.isArray(content)) return [];
        return content.map((_: any) => {
            return { key: _[key], label: _[label] };
        });
    },
    // 배열에 추가 ( array , [ item ] )
    addArray: (arr: Array<any>, append: Array<any>) => {
        arr.push(...append);
    },
    // 배열에 필터링 ( array , { a : 1 } )
    // a 가 1 인 데이터만 리턴
    filterArray: (arr: Array<any>, find: { [key: string]: any }) => {
        return lodash.filter(arr, find);
    },
    // 배열에서 검색 ( array , { a : 1 } )
    // a 가 1 인 데이터의 index 를 리턴 ( 없으면 -1 )
    findArray: (arr: Array<any>, find: { [key: string]: any }) => {
        return lodash.findIndex(arr, find);
    },
    // 배열에서 삭제 ( array , { a : 1 } )
    // a 가 1 인 데이터를 찾아서 삭제
    deleteArray: (arr: Array<any>, find: { [key: string]: any }) => {
        lodash.remove(arr, (item) => {
            return lodash.findIndex([item], find) !== -1;
        });
    },
    // 배열 수정 ( array , { a : 1 } , { a : 1 , b : 2 } )
    // a 가 1 인 데이터를 { a : 1 , b : 2 }로 수정
    updateArray: (arr: Array<any>, find: { [key: string]: any }, update: any) => {
        let index = lodash.findIndex(arr, find);
        if (index > -1) {
            arr[index] = update;
        }
    },
    cloneDeep: (obj: any) => {
        return lodash.cloneDeep(obj);
    },
    trim: (val: string) => {
        return lodash.trim(val);
    },
    importExcel: async (arg: { url: string; data?: any }) => {
        return new Promise((resolve) => {
            const input = document.createElement("input");
            input.type = "file";
            const url = arg.url;
            const data = arg.data ? arg.data : {};
            input.onchange = async (event: any) => {
                const file = event.target.files[0];
                const formData = new FormData();
                formData.append("file", file);
                formData.append("dto", new Blob([JSON.stringify(data)], { type: "application/json" }));
                try {
                    const response = await api.post(url, formData);
                    resolve(response);
                } catch (e) {
                    resolve({ status: 500, data: e });
                }
            };
            input.click();
        });
    },
    exportExcels: (arg: {
        excelName: string;
        sheets: Array<{
            name: string;
            sheet: any;
        }>;
    }) => {
        const { excelName, sheets } = arg;
        const wb = xlsxUtils.book_new();

        sheets.map(({ name, sheet }, index) => {
            xlsxUtils.book_append_sheet(wb, sheet, name);
        });
        writeFile(wb, excelName + ".xlsx");
    },
    isValidDate: (date: string) => {
        return !isNaN(Date.parse(date));
    },
    dateDiff: (start: Date, end: Date, part: QUnitType | OpUnitType = "d") => {
        if (start === undefined || end === undefined) return 0;
        if (start === null || end === null) return 0;

        const ds = dayjs(start);
        const de = dayjs(end);
        return ds.diff(de, part);
    },
    getBlob: async (data: any, isPublic?: boolean) => {
        let sys = "",
            base = "";
        base = "api/v1/ptle/comn/files";
        sys = process.env.REACT_APP_API_OSCI || "";

        const fileGetUrl = `${sys}/${base}` + (isPublic === true ? "/public" : "");
        let fileObject = null;

        try {
            const response = await api.get(fileGetUrl + `/${data.fileGrpUuid}`);
            fileObject = response.data.fileDtlInfoList.content;
        } catch (error) {
            //console.log(error);
        }

        const fileDownloadUrl = `${sys}/${base}` + (isPublic === true ? "/public" : "") + `/download`;
        try {
            const response = await api.post(
                fileDownloadUrl,
                {
                    fileGrpUuid: data.fileGrpUuid,
                    fileUuid: fileObject[0].fileUuid,
                },
                { responseType: "blob" },
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getManual: async (data: any) => {
        let sys = "",
            base = "";
        base = "api/v1/ptle/comn/files";
        sys = process.env.REACT_APP_API_OSCI || "";
        const isPublic = true;
        const fileDownloadUrl = isPublic ? `${sys}/${base}/public/download` : `${sys}/${base}/download`;

        try {
            const response = await api.post(
                fileDownloadUrl,
                {
                    fileGrpUuid: data.fileGrpUuid,
                    fileUuid: data.fileUuid,
                },
                { responseType: "arraybuffer" },
            );

            let filename = response.headers["content-disposition"]
                .split("; ")
                .find((str: any) => str?.startsWith("filename="));
            if (filename) filename = filename.slice(9).replaceAll('"', "");
            const result = { buffer: response.data, __t: new Date(), filename };

            return result;
        } catch (error) {
            console.log(error);
            throw error;
        }
    },
    downloadTemplate: async (templateId: any, isPublic?: boolean) => {
        let sys = "",
            base = "",
            publicYn = isPublic ? "/public" : "";
        base = "api/v1/osci/comn/files";
        sys = process.env.REACT_APP_API_OSCI || "";

        let fileGetUrl = `${sys}/${base}${publicYn}`;
        let fileDownloadUrl = `${sys}/${base}${publicYn}/download`;

        let data;
        try {
            const response = await api.get(fileGetUrl + `/${templateId}`);
            data = response.data.fileDtlInfoList.content[0];
        } catch (error) {
            console.log(error);
            return;
        }

        try {
            const response = await api.post(
                fileDownloadUrl,
                {
                    fileGrpUuid: data.fileGrpUuid,
                    fileUuid: data.fileUuid,
                },
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            const nm: string = data.fileNm || "";
            const ext: string = data.fileExt ? "." + data.fileExt : "";

            a.download = nm.endsWith(ext) ? nm : nm + ext;
            a.href = url;
            a.click();
            a.remove();
        } catch (error) {
            console.log(error);
        }
    },
    exportExcel: async (arg: { url: string; fileName?: string; data?: any; ext?: "csv" | "xlsx" }) => {
        try {
            const response = await api.post(
                arg.url,
                {
                    ...arg.data,
                },
                { responseType: "blob" },
            );

            if (arg.fileName === undefined) arg.fileName = "Export";
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            const nm: string = arg.fileName || "";
            const ext: string = arg.ext ?? "xlsx";

            a.download = nm.split(".")[0] + `.${ext}`;
            a.href = url;
            a.click();
            a.remove();
        } catch (error) {
            console.log(error);
        }
    },
};

export { comnEnvs };
export const envs = comnEnvs;
export const utils = comnUtils;

export default { envs, utils };
